'use client'

import React, { useState, useRef, useEffect, useCallback } from 'react'
import AppTooltipWrapper from './molecules/AppTooltipWrapper'
import { TYPES } from './AppIcon'
import { withRequiredAsterisk } from '@/app/utils/required-label'

interface TypeaheadOption {
  label: string
  value: string
}

interface UnitOption {
  label: string
  value: string
  icon?: string
}

interface AppFormInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string
  error?: { message?: string }
  icon?: React.ReactNode
  wrapperClassName?: string
  tooltip?: {
    title?: string
    text: string
    iconClassName?: string
    iconType?: keyof typeof TYPES
  }
  /** Static unit displayed to the right of the input (e.g. "%", "mois") */
  unit?: string
  /** Selectable units displayed as dropdown to the right of the input (e.g. EUR/CHF) */
  unitOptions?: UnitOption[]
  /** Currently selected unit value */
  selectedUnit?: string
  /** Callback when unit changes */
  onUnitChange?: (unit: string) => void
  /** Show a clear (X) button on the right */
  showClearButton?: boolean
  /** Callback when clear button is clicked */
  onClear?: () => void
  /** Typeahead options list */
  typeaheadOptions?: TypeaheadOption[]
  /** Callback when a typeahead option is selected */
  onTypeaheadSelect?: (option: TypeaheadOption) => void
  /** Use the loan-comparator blue border design */
  variant?: 'default' | 'loan' | 'health'
}

const AppFormInput = React.forwardRef<HTMLInputElement, AppFormInputProps>(
  (
    {
      label,
      error,
      icon,
      tooltip,
      wrapperClassName = '',
      className = '',
      id,
      unit,
      unitOptions,
      selectedUnit,
      onUnitChange,
      showClearButton,
      onClear,
      typeaheadOptions,
      onTypeaheadSelect,
      variant = 'default',
      onChange,
      value,
      ...props
    },
    ref
  ) => {
    const [typeaheadOpen, setTypeaheadOpen] = useState(false)
    const [typeaheadFilter, setTypeaheadFilter] = useState('')
    const [unitDropdownOpen, setUnitDropdownOpen] = useState(false)
    const [highlightedIndex, setHighlightedIndex] = useState(-1)
    const typeaheadRef = useRef<HTMLDivElement>(null)
    const unitDropdownRef = useRef<HTMLDivElement>(null)

    const isLoan = variant === 'loan'
    const isHealth = variant === 'health'

    const hasAddon = !!unit || (unitOptions && unitOptions.length > 0) || !!icon
    const hasRightSection = hasAddon || showClearButton

    // Filter typeahead options based on input
    const filteredTypeaheadOptions = typeaheadOptions
      ? typeaheadOptions.filter((opt) =>
          opt.label.toLowerCase().includes(typeaheadFilter.toLowerCase())
        )
      : []

    // Close dropdowns on outside click
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (typeaheadRef.current && !typeaheadRef.current.contains(event.target as Node)) {
          setTypeaheadOpen(false)
        }
        if (unitDropdownRef.current && !unitDropdownRef.current.contains(event.target as Node)) {
          setUnitDropdownOpen(false)
        }
      }
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (typeaheadOptions) {
          setTypeaheadFilter(e.target.value)
          setTypeaheadOpen(true)
          setHighlightedIndex(-1)
        }
        onChange?.(e)
      },
      [onChange, typeaheadOptions]
    )

    const handleTypeaheadSelect = useCallback(
      (option: TypeaheadOption) => {
        setTypeaheadOpen(false)
        setTypeaheadFilter('')
        onTypeaheadSelect?.(option)
      },
      [onTypeaheadSelect]
    )

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!typeaheadOpen || !filteredTypeaheadOptions.length) return

        if (e.key === 'ArrowDown') {
          e.preventDefault()
          setHighlightedIndex((prev) =>
            prev < filteredTypeaheadOptions.length - 1 ? prev + 1 : 0
          )
        } else if (e.key === 'ArrowUp') {
          e.preventDefault()
          setHighlightedIndex((prev) =>
            prev > 0 ? prev - 1 : filteredTypeaheadOptions.length - 1
          )
        } else if (e.key === 'Enter' && highlightedIndex >= 0) {
          e.preventDefault()
          handleTypeaheadSelect(filteredTypeaheadOptions[highlightedIndex])
        } else if (e.key === 'Escape') {
          setTypeaheadOpen(false)
        }
      },
      [typeaheadOpen, filteredTypeaheadOptions, highlightedIndex, handleTypeaheadSelect]
    )

    // Border classes based on variant and error state
    const borderClass = error
      ? 'border-invalidRed'
      : isLoan
      ? 'border-rm-gray-light hover:border-rm-gray focus-within:border-rm-gray'
      : isHealth
      ? 'border-blue-300 hover:border-blue-500 focus-within:border-blue-500'
      : 'border-gray-300'

    const labelColorClass = isLoan
      ? 'text-pale'
      : isHealth
      ? 'text-blue-800'
      : 'text-gray-700'

    const labelSizeClass = isHealth ? 'text-[12px] font-semibold' : 'text-[14px] font-medium'

    const containerClass = `flex items-center border ${borderClass} rounded-rm-10 overflow-hidden transition-all duration-300 ${
      isHealth || isLoan ? 'min-h-12 bg-white' : ''
    }`

    const inputVariantClass = isLoan
      ? 'text-pale text-[14px] py-3'
      : isHealth
      ? 'text-blue-900 text-[14px] py-3'
      : ''

    return (
      <div className={`form-group ${wrapperClassName}`}>
        {/* Label with optional tooltip */}
        {label && (
          <>
            {tooltip ? (
              <AppTooltipWrapper
                id={id || label}
                title={tooltip.title}
                text={tooltip.text}
                iconClassName={tooltip.iconClassName || 'fa-info-circle'}
                iconType={tooltip.iconType || 'regular'}
                theme={isLoan ? 'loan' : undefined}
              >
                <label htmlFor={id} className={`${labelSizeClass} ${labelColorClass} p-1`}>
                  {withRequiredAsterisk(label)}
                </label>
              </AppTooltipWrapper>
            ) : (
              <label htmlFor={id} className={`block ${labelSizeClass} ${labelColorClass} p-1`}>
                {withRequiredAsterisk(label)}
              </label>
            )}
          </>
        )}

        {/* Input with addons */}
        <div className="relative" ref={typeaheadOptions ? typeaheadRef : undefined}>
          <div className={containerClass}>
            <input
              id={id}
              ref={ref}
              value={value}
              className={`flex-1 px-3 py-2 focus:outline-none bg-transparent ${inputVariantClass} ${className}`}
              onChange={handleInputChange}
              onFocus={() => {
                if (typeaheadOptions && typeaheadFilter) setTypeaheadOpen(true)
              }}
              onKeyDown={typeaheadOptions ? handleKeyDown : undefined}
              {...props}
            />

            {/* Clear button */}
            {showClearButton && (
              <button
                type="button"
                onClick={onClear}
                className="px-2 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Effacer"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}

            {/* Static unit addon */}
            {unit && !unitOptions && (
              <span className={`px-3 self-stretch flex items-center text-[14px] font-semibold border-l ${
                isLoan
                  ? 'bg-gray-100 border-rm-gray-light text-pale'
                  : 'bg-gray-100 border-gray-300 text-gray-600'
              }`}>
                {unit}
              </span>
            )}

            {/* Unit selector dropdown */}
            {unitOptions && unitOptions.length > 0 && (
              <div
                className="relative"
                ref={unitDropdownRef}
                tabIndex={-1}
                onBlur={(e) => {
                  if (!unitDropdownRef.current?.contains(e.relatedTarget as Node)) {
                    setUnitDropdownOpen(false)
                  }
                }}
              >
                <button
                  type="button"
                  onClick={() => setUnitDropdownOpen(!unitDropdownOpen)}
                  className={`px-3 self-stretch text-[14px] font-semibold border-l flex items-center gap-1.5 cursor-pointer ${
                    isLoan
                      ? 'bg-gray-100 border-rm-gray-light text-pale hover:bg-gray-200'
                      : 'bg-gray-100 border-gray-300 text-gray-600 hover:bg-gray-200'
                  } transition-colors`}
                >
                  {(() => {
                    const selected = unitOptions.find((o) => o.value === selectedUnit) || unitOptions[0]
                    return selected?.icon
                      ? <i className={`fas fa-${selected.icon}`} />
                      : <span>{selected?.label}</span>
                  })()}
                  <i className={`fa-solid fa-caret-down transition-transform duration-300 text-xs ${unitDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {unitDropdownOpen && (
                  <div className="absolute right-[-1px] top-[calc(100%-2px)] bg-blue-100 border border-blue-300 rounded-b z-[300] min-w-[calc(100%+2px)] flex flex-col">
                    {unitOptions.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => {
                          onUnitChange?.(opt.value)
                          setUnitDropdownOpen(false)
                        }}
                        className={`w-full text-left px-2.5 py-1.5 text-[14px] flex items-center justify-between gap-2 transition-colors ${
                          selectedUnit === opt.value
                            ? 'bg-blue-300'
                            : 'hover:bg-blue-200'
                        }`}
                      >
                        {opt.icon && <i className={`fas fa-${opt.icon}`} />}
                        <span>{opt.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Icon addon (only when no unit/unitOptions) */}
            {icon && !unit && !unitOptions && (
              <div className={`px-3 self-stretch flex items-center justify-center border-l ${
                isLoan
                  ? 'bg-gray-100 border-rm-gray-light text-pale'
                  : isHealth
                  ? 'bg-blue-100 border-blue-300 text-blue-800'
                  : ''
              }`}>
                {icon}
              </div>
            )}
          </div>

          {/* Typeahead dropdown */}
          {typeaheadOpen && filteredTypeaheadOptions.length > 0 && (
            <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-rm-gray-light rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
              {filteredTypeaheadOptions.map((option, index) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleTypeaheadSelect(option)}
                  className={`w-full text-left px-4 py-2 text-[14px] transition-colors ${
                    index === highlightedIndex
                      ? 'bg-blue-600 text-white'
                      : 'text-pale hover:bg-blue-50'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Error message */}
        {error?.message && (
          <p className="mt-1 text-[12px] text-red-600">{error.message}</p>
        )}
      </div>
    )
  }
)

AppFormInput.displayName = 'AppFormInput'

export default AppFormInput
